import request from '@/modules/huyu-framework/backend/utils/request'
import { getToken } from '@/modules/huyu-framework/backend/utils/auth'
import { createFetchTransport } from '@huyu/core/ai'

// 获取可用智能体列表
export function listAgents() {
  return request({
    url: '/v1/models',
    method: 'get'
  })
}

// 与智能体对话（SSE 流式）—— 传输与协议解析统一走 @huyu/core 内核
// 支持通过 apiKey 参数使用 X-API-Key 认证（外部集成场景）
export async function* chatWithAgent(data, signal, apiKey) {
  // 走 apiKey 时不能再带 Authorization，否则网关会按 JWT 分支校验失败
  const transport = createFetchTransport({
    baseUrl: import.meta.env.VITE_APP_BASE_API,
    getToken: apiKey ? () => null : getToken,
    headers: apiKey ? { 'X-API-Key': apiKey } : undefined
  })
  yield* transport.streamChat(data, signal)
}


// 获取会话列表
export function listChatRecord(query) {
  return request({
    url: '/huyuAI/chat/list',
    method: 'get',
    params: query
  })
}

// 获取会话详情
export function getChat(chatId) {
  return request({
    url: '/huyuAI/chat/' + chatId,
    method: 'get'
  })
}

// 查询用户历史会话（默认只查询个人，viewDept=true 且有权限时可查看部门会话）
// 是否只展示用户真实对话（conversation）由调用方决定，通过 query.recordType 传入
export function getListByUserApi(query) {
  return request({
    url: '/huyuAI/chat/list',
    method: 'get',
    params: query
  })
}

// 新增会话
export function addChat(data) {
  return request({
    url: '/huyuAI/chat',
    method: 'post',
    data: data
  })
}

// 修改会话
export function updateChat(data) {
  return request({
    url: '/huyuAI/chat',
    method: 'put',
    data: data
  })
}

// 删除会话
export function deleteChatRecord(chatIds) {
  return request({
    url: '/huyuAI/chat/' + chatIds,
    method: 'delete'
  })
}

// 删除会话（按当前用户接口）
export function deleteChatApi(chatId) {
  return request({
    url: '/huyuAI/chat/' + chatId,
    method: 'delete'
  })
}

// 查询会话完整对话记录（所有轮次的问答）
export function getConversationHistory(chatId) {
  return request({
    url: '/huyuAI/chat/history',
    method: 'get',
    params: { chatId: chatId }
  })
}

// 查询会话Token用量列表
export function listChatTokenUsage(query) {
  return request({
    url: '/huyuAI/chat/tokenList',
    method: 'get',
    params: query
  })
}

// 查询提问记录列表（按chatId）
export function listQuestionRecord(query) {
  return request({
    url: '/huyuAI/questionRecord/list',
    method: 'get',
    params: query
  })
}

// 获取历史问答内容（OpenAI格式）
// 是否只展示用户真实对话（conversation）由调用方决定，通过 query.recordType 传入
export function getHistoryChatApi(chatId, query) {
  if (chatId == undefined || chatId == null || chatId == '') return
  return request({
    url: '/huyuAI/chat/history',
    method: 'get',
    params: { chatId: chatId, ...query }
  })
}

// ============ 统一对话接口（OpenAI兼容 + 增强参数） ============
// POST /v1/chat/completions
// 支持标准 OpenAI 参数：model, messages, stream, chat_id
// 增强参数：agent_id, knowledge_ids, rag_type
//
// 🔁 2026-10-28：底部从「本文件手写 fetch + getReader + 逐行 SSE 解析」改为
//    **@huyu/core 内核 createFetchTransport**（唯一真源）。
//    这样后台与 AI 前台、渔业、壳子跑的是同一份传输与协议实现；
//    品牌检查（H5 fetch 只认原生 AbortSignal）、错误体解析、[DONE] 收尾
//    全部由内核负责，本文件只保留本端专属的配置（baseUrl / token / 图片兜底）。
export async function* chatWithMultiVector(payload, signal) {
  // 兼容旧调用方：内核只认已转好的 imageDataUrl，传 File 的在这里兜一次
  let imageDataUrl = payload.imageDataUrl
  if (!imageDataUrl && payload.imageFile) {
    imageDataUrl = await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.readAsDataURL(payload.imageFile)
    })
  }

  const transport = createFetchTransport({
    baseUrl: import.meta.env.VITE_APP_BASE_API,
    getToken
  })

  yield* transport.streamChat({ ...payload, imageDataUrl }, signal)
}


// ============ 非流式对话接口（OpenAI兼容） ============
// POST /v1/chat/completions with stream=false
// 返回完整 ChatCompletion JSON 响应
export async function chatCompletion(payload, signal) {
  const token = getToken()

  // Build message content: support multi-modal (text + image)
  let messageContent
  if (payload.imageFile) {
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.readAsDataURL(payload.imageFile)
    })
    const parts = []
    if (payload.question) {
      parts.push({ type: 'text', text: payload.question })
    }
    parts.push({
      type: 'image_url',
      image_url: { url: dataUrl }
    })
    messageContent = parts
  } else {
    messageContent = payload.question || ''
  }

  // 🔁 2026-10-28：调用方可能已经拼好完整 messages（多轮历史），此时必须以它为真源。
  //    旧实现无条件用 payload.question 重建单条消息，导致 PageAiAssist 这类只传
  //    messages 的调用方发出 `messages:[{content:""}]`，后端拿不到问题。
  //    model 同理：后端 AgentRouter 只按 agentCode / modelCode 解析，
  //    传 'default' 会直接 500（Agent not found: default），所以 agentCode 优先。
  const hasOwnMessages = Array.isArray(payload.messages) && payload.messages.length > 0

  const body = {
    model: payload.model || payload.agentCode || 'default',
    messages: hasOwnMessages ? payload.messages : [{ role: 'user', content: messageContent }],
    stream: false,
    // 透传标准 OpenAI 参数
    temperature: payload.temperature || undefined,
    max_tokens: payload.maxTokens || undefined,
    top_p: payload.topP || undefined,
    tools: payload.tools || undefined,
    tool_choice: payload.toolChoice || undefined,
    // 增强参数
    chat_id: payload.chatId || undefined,
    agent_id: payload.agentId || undefined,
    knowledge_ids: payload.knowledgeIds || undefined,
    rag_type: payload.ragType || undefined,
    // 恒发 boolean：`|| undefined` 会把 false 吃掉（undefined 由下方清理移除，false 必须保留）
    thinking: payload.thinking
  }
  Object.keys(body).forEach(k => body[k] === undefined && delete body[k])

  const response = await fetch(import.meta.env.VITE_APP_BASE_API + '/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
    },
    body: JSON.stringify(body),
    signal
  })
  if (!response.ok) {
    let errorMsg = '请求失败：HTTP ' + response.status
    try {
      const text = await response.text()
      if (text) {
        try {
          const parsed = JSON.parse(text)
          if (parsed.error) {
            errorMsg = parsed.error.message || errorMsg
          }
        } catch {
          errorMsg = text
        }
      }
    } catch (e) {}
    throw new Error(errorMsg)
  }
  return await response.json()
}
